import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const LogsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    data: '',
    projeto: '',
    projetoId: '',
    atualizacoes: [],
    ordem: 0,
    tags: [],
    ativo: true
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projetos, setProjetos] = useState([]);
  const [newAtualizacao, setNewAtualizacao] = useState({ texto: '', tipo: 'texto', link: '' });
  const [newTag, setNewTag] = useState('');

  // Buscar dados do log se estiver em modo de edição
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        try {
          setLoading(true);
          const response = await api.get(`/api/logs/${id}`);
          setFormData(response.data.data.log);
        } catch (error) {
          console.error('Erro ao buscar log:', error);
          toast.error('Erro ao carregar dados do log');
          navigate('/admin/logs');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, isEditMode, navigate]);

  // Buscar projetos disponíveis
  useEffect(() => {
    const fetchProjetos = async () => {
      try {
        const response = await api.get('/api/projetos');
        setProjetos(response.data.data.projetos);
      } catch (error) {
        console.error('Erro ao buscar projetos:', error);
      }
    };

    fetchProjetos();
  }, []);

  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Manipular mudanças nos campos da nova atualização
  const handleNewAtualizacaoChange = (e) => {
    const { name, value } = e.target;
    setNewAtualizacao({
      ...newAtualizacao,
      [name]: value
    });
  };

  // Adicionar nova atualização
  const handleAddAtualizacao = () => {
    if (newAtualizacao.texto.trim()) {
      setFormData({
        ...formData,
        atualizacoes: [...formData.atualizacoes, { ...newAtualizacao }]
      });
      setNewAtualizacao({ texto: '', tipo: 'texto', link: '' });
    }
  };

  // Remover atualização
  const handleRemoveAtualizacao = (index) => {
    const updatedAtualizacoes = [...formData.atualizacoes];
    updatedAtualizacoes.splice(index, 1);
    setFormData({
      ...formData,
      atualizacoes: updatedAtualizacoes
    });
  };

  // Adicionar tag
  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  // Remover tag
  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      if (isEditMode) {
        await api.patch(`/api/logs/${id}`, formData);
        toast.success('Log atualizado com sucesso!');
      } else {
        await api.post('/api/logs', formData);
        toast.success('Log criado com sucesso!');
      }
      
      navigate('/admin/logs');
    } catch (error) {
      console.error('Erro ao salvar log:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar log');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" style={{borderTopColor: '#0066FF'}}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with glassmorphism */}
      <div style={{
        background: 'rgba(30, 30, 30, 0.25)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
      }} className="rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-3xl">{isEditMode ? '✏️' : '✨'}</span>
            <h1 className="text-3xl font-bold text-white font-display">
              {isEditMode ? 'Editar Log' : 'Novo Log'}
            </h1>
          </div>
          <Link
            to="/admin/logs"
            className="px-6 py-3 bg-gray-700/50 text-gray-300 rounded-xl hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm"
          >
            ← Voltar
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{
        background: 'rgba(30, 30, 30, 0.25)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
      }} className="rounded-2xl p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Data */}
          <div>
            <label htmlFor="data" className="block text-sm font-medium text-gray-300 mb-1">
              Data *
            </label>
            <input
              type="text"
              id="data"
              name="data"
              value={formData.data}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
              placeholder="📅 Ex: 01/06/2025"
            />
          </div>

          {/* Projeto */}
          <div>
            <label htmlFor="projeto" className="block text-sm font-medium text-gray-300 mb-1">
              Projeto *
            </label>
            <select
              id="projeto"
              name="projeto"
              value={formData.projeto}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
            >
              <option value="">📁 Selecione um projeto</option>
              {projetos.map((projeto) => (
                <option key={projeto._id} value={projeto.nome}>
                  {projeto.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Ordem */}
          <div>
            <label htmlFor="ordem" className="block text-sm font-medium text-gray-300 mb-1">
              Ordem
            </label>
            <input
              type="number"
              id="ordem"
              name="ordem"
              value={formData.ordem}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
              placeholder="🔢 Ordem de exibição"
            />
          </div>

          {/* Ativo */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="ativo"
              name="ativo"
              checked={formData.ativo}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500/50 border-gray-600 rounded bg-gray-800/50 backdrop-blur-sm"
            />
            <label htmlFor="ativo" className="ml-2 block text-sm text-gray-300">
              Ativo
            </label>
          </div>

          {/* Atualizações */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Atualizações *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="texto"
                  value={newAtualizacao.texto}
                  onChange={handleNewAtualizacaoChange}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
                  placeholder="📝 Texto da atualização"
                />
              </div>
              <div className="flex">
                <select
                  name="tipo"
                  value={newAtualizacao.tipo}
                  onChange={handleNewAtualizacaoChange}
                  className="w-1/3 px-3 py-3 bg-gray-800/50 border border-gray-600/50 rounded-l-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
                >
                  <option value="texto">Texto</option>
                  <option value="link">Link</option>
                  <option value="codigo">Código</option>
                  <option value="imagem">Imagem</option>
                </select>
                <button
                  type="button"
                  onClick={handleAddAtualizacao}
                  style={{background: 'linear-gradient(90deg, #0066FF, #6E44FF)'}}
                  className="w-2/3 px-4 py-3 text-white rounded-r-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
                >
                  ➕ Adicionar
                </button>
              </div>
            </div>
            {newAtualizacao.tipo === 'link' && (
              <div className="mb-2">
                <input
                  type="url"
                  name="link"
                  value={newAtualizacao.link}
                  onChange={handleNewAtualizacaoChange}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
                  placeholder="🔗 URL do link"
                />
              </div>
            )}
            {formData.atualizacoes.length > 0 ? (
              <ul className="space-y-2 mt-2">
                {formData.atualizacoes.map((atualizacao, index) => (
                  <li key={index} className="flex items-center bg-gray-700 p-2 rounded-md">
                    <div className="flex-1">
                      <div className="text-white">{atualizacao.texto}</div>
                      {atualizacao.tipo === 'link' && atualizacao.link && (
                        <div className="text-indigo-400 text-sm">{atualizacao.link}</div>
                      )}
                      <div className="text-gray-400 text-xs">Tipo: {atualizacao.tipo}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAtualizacao(index)}
                      className="ml-2 text-red-400 hover:text-red-300 focus:outline-none"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-sm mt-2">Nenhuma atualização adicionada.</p>
            )}
          </div>

          {/* Tags */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Tags
            </label>
            <div className="flex mb-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-l-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 backdrop-blur-sm"
                placeholder="🏷️ Adicionar tag"
              />
              <button
                type="button"
                onClick={handleAddTag}
                style={{background: 'linear-gradient(90deg, #0066FF, #6E44FF)'}}
                className="px-4 py-3 text-white rounded-r-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
              >
                ➕ Adicionar
              </button>
            </div>
            {formData.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-400 hover:text-blue-300 focus:outline-none"
                    >
                      <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm mt-2">Nenhuma tag adicionada.</p>
            )}
          </div>
        </div>

        {/* Botões */}
        <div className="mt-6 flex justify-end space-x-3">
          <Link
            to="/admin/logs"
            className="px-6 py-3 bg-gray-700/50 text-gray-300 rounded-xl hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm"
          >
            ❌ Cancelar
          </Link>
          <button
            type="submit"
            disabled={submitting}
            style={{background: 'linear-gradient(90deg, #0066FF, #6E44FF)'}}
            className="px-6 py-3 text-white rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-50 transition-all duration-300 shadow-lg"
          >
            {submitting ? '🔄 Salvando...' : '✨ Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogsForm;
